import { User } from '@choco-admin/types';
import { useCallback, useState, useMemo } from 'react';

import {
  type UsersPageSearchUsersQuery,
  useUsersPageSearchUsersLazyQuery,
} from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type SearchUsersParams = {
  keywords?: string;
  filter?: {
    isPuppy?: boolean;
  };
  limit?: number;
  after?: string;
};

export type PageInfo = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
};

export type UseSearchUsersResponse = {
  users: User[];
  totalUsers: number;
  isLoading: boolean;
  hasNextPage: boolean;
  pageInfo: PageInfo;
  searchUsers: (params: SearchUsersParams) => Promise<void>;
  goToNextPage: () => Promise<void>;
  goToPreviousPage: () => Promise<void>;
  currentPage: number;
  refetch: () => Promise<void>;
};

export type UseSearchUsersConfig = {
  initialPageSize: number;
  captureException?: CaptureExceptionFunction;
};

type UserSearchConnectionResult = UsersPageSearchUsersQuery['userSearch'];

const EMPTY_PAGE_INFO: PageInfo = {
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: '',
  endCursor: '',
};

const mapUsers = (
  result: UserSearchConnectionResult | null | undefined
): { users: User[]; total: number; pageInfo: PageInfo } => {
  if (!result || result.__typename !== 'UserSearchConnection') {
    return { users: [], total: 0, pageInfo: EMPTY_PAGE_INFO };
  }

  const { edges, total, pageInfo } = result;

  try {
    const users = edges.map((edge) => mapToUser(edge.node));
    return {
      users,
      total,
      pageInfo: {
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor,
      },
    };
  } catch {
    return { users: [], total: 0, pageInfo: EMPTY_PAGE_INFO };
  }
};

export const useSearchUsers = (
  config: UseSearchUsersConfig
): UseSearchUsersResponse => {
  const { captureException, initialPageSize } = config;

  /**
   * Tracks the base search params (keywords, filter, limit) separate from the
   * cursor so that page navigation can reuse the same query without the caller
   * having to pass them again.
   */
  const [baseParams, setBaseParams] = useState<SearchUsersParams | null>(null);

  /**
   * Cursor stack — each entry is the endCursor of the page at that index.
   * Index 0 means "page 1, no cursor" (undefined).
   * The current page is cursorStack.length (1-based).
   */
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([
    undefined,
  ]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [executeQuery, { data, loading }] = useUsersPageSearchUsersLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const executeWithCursor = useCallback(
    async (params: SearchUsersParams, cursor: string | undefined) => {
      const effectivePageSize = params.limit ?? initialPageSize;

      const query = {
        ...(params.keywords && { keywords: params.keywords }),
        filter: {
          ...params.filter,
        },
      };

      await executeQuery({
        variables: {
          query,
          limit: effectivePageSize,
          after: cursor,
        },
      });
    },
    [executeQuery, initialPageSize]
  );

  const searchUsers = useCallback(
    async (params: SearchUsersParams) => {
      try {
        // New search: reset cursor stack to page 1
        setBaseParams(params);
        setCursorStack([undefined]);
        setCurrentPageIndex(0);
        await executeWithCursor(params, undefined);
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [executeWithCursor, captureException]
  );

  const goToNextPage = useCallback(async () => {
    if (!baseParams) return;

    const { pageInfo: currentPageInfo } = mapUsers(data?.userSearch);

    if (!currentPageInfo.hasNextPage || !currentPageInfo.endCursor) return;

    try {
      const nextCursor = currentPageInfo.endCursor;
      const nextIndex = currentPageIndex + 1;

      // If we've navigated here before, the cursor is already in the stack;
      // otherwise push the new cursor.
      setCursorStack((prev) => {
        if (nextIndex < prev.length) return prev;
        return [...prev, nextCursor];
      });
      setCurrentPageIndex(nextIndex);

      await executeWithCursor(baseParams, nextCursor);
    } catch (error) {
      captureException?.(error as Error);
      throw error;
    }
  }, [baseParams, data, currentPageIndex, executeWithCursor, captureException]);

  const goToPreviousPage = useCallback(async () => {
    if (!baseParams || currentPageIndex === 0) return;

    try {
      const prevIndex = currentPageIndex - 1;
      const prevCursor = cursorStack[prevIndex];

      setCurrentPageIndex(prevIndex);
      await executeWithCursor(baseParams, prevCursor);
    } catch (error) {
      captureException?.(error as Error);
      throw error;
    }
  }, [
    baseParams,
    currentPageIndex,
    cursorStack,
    executeWithCursor,
    captureException,
  ]);

  const refetch = useCallback(async () => {
    if (!baseParams) return;
    const currentCursor = cursorStack[currentPageIndex];
    await executeWithCursor(baseParams, currentCursor);
  }, [baseParams, cursorStack, currentPageIndex, executeWithCursor]);

  const { users, total, pageInfo } = useMemo(
    () => mapUsers(data?.userSearch),
    [data?.userSearch]
  );

  return useMemo(
    () => ({
      users: users || [],
      totalUsers: total,
      isLoading: loading,
      hasNextPage: pageInfo.hasNextPage,
      pageInfo,
      currentPage: currentPageIndex + 1,
      searchUsers,
      goToNextPage,
      goToPreviousPage,
      refetch,
    }),
    [
      users,
      total,
      loading,
      pageInfo,
      currentPageIndex,
      searchUsers,
      goToNextPage,
      goToPreviousPage,
      refetch,
    ]
  );
};
